import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useContext } from "react";
import { SnackbarContext } from "context/snackBarContext";
import { useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import LayoutAccessManagement from "components/structure/LayoutAccessManagement";
import { useTranslation } from "react-i18next";
import ReportsTable from "./ReportsTable";
import CustomPagination from "../../Import/Common/CustomPagination";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ExportConfirmModal from "./ExportConfirmModal";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import ReportsService from "services/ReportsService";
import NoData from "assets/noData.svg";
import dayjs from "dayjs";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

const commonDateStyle = {
  fontFamily: "Montserrat",
  fontSize: "16px",
  lineHeight: "150%",
  letterSpacing: "0%",
  fontWeight: 500,
  fontStyle: "Medium",
};

const commonStyle = {
  fontFamily: "Montserrat",
  fontSize: "14.12px",
  lineHeight: "150%",
  letterSpacing: "0%",
  fontWeight: 600,
  fontStyle: "SemiBold",
};

const GetReports = () => {
  const { t } = useTranslation();
  const exportData = useRef(null);
  const [auditLevel, setAuditLevel] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(5);
  const [exportType, setExportType] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState();
  const [exportConfirmModal, setExportConfirmModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState(0);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(true);
  const [selectedBox, setSelectedBox] = useState(false);
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);

  const Level0columns = [
    {
      label: t("reports.sno"),
      field: "sno",
      width: "35px",
    },
    {
      label: null,
      field: "checked",
      width: "40px",
    },
    {
      label: t("reports.batOneId"),
      field: "batOneId",
      width: "70px",
    },
    {
      label: t("reports.userName"),
      field: "userName",
      width: "80px",
    },
    {
      label: t("reports.accountCreation"),
      field: "accountCreation",
      width: "100px",
    },
    {
      label: t("reports.activeRoles"),
      field: "activeRoles",
      width: "120px",
    },
    {
      label: t("reports.accountStatus"),
      field: "accountStatus",
      width: "80px",
    },
    {
      label: t("reports.accountInactivation"),
      field: "accountInactivation",
      width: "100px",
    },
    {
      label: t("reports.lastLoginDate"),
      field: "lastLoginDate",
      width: "100px",
    },
  ];

  const Level1columns = [
    {
      label: t("reports.sno"),
      field: "sno",
      width: "35px",
    },
    {
      label: null,
      field: "checked",
      width: "40px",
    },
    {
      label: t("reports.batOneId"),
      field: "batOneId",
      width: "90px",
    },
    {
      label: t("reports.userName"),
      field: "userName",
      width: "80px",
    },
    {
      label: t("reports.functionality"),
      field: "functionality",
      width: "100px",
    },
    {
      label: t("Action"),
      field: "action",
      width: "70px",
    },
    {
      label: t("reports.entityTableName"),
      field: "entityTableName",
      width: "80px",
    },
    {
      label: t("reports.primaryId"),
      field: "primaryId",
      width: "80px",
    },
    {
      label: t("reports.fieldName"),
      field: "fieldName",
      width: "90px",
    },
    {
      label: t("reports.oldValue"),
      field: "oldValue",
      width: "70px",
    },
    {
      label: t("reports.newValue"),
      field: "newValue",
      width: "70px",
    },
    {
      label: t("reports.dateTime"),
      field: "dateTime",
      width: "90px",
    },
  ];

  const [columns, setColumns] = useState(Level0columns);

  const paginate = (pageNumber, pageSize) => {
    setData([]);
    setCurrentPage(pageNumber - 1);
    fetchResults(searchQuery, pageNumber - 1, pageSize);
  };

  const timeZoneNote = () => {
    const offset = new Date().getTimezoneOffset(); // in minutes
    const sign = offset <= 0 ? "+" : "-";
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const minutes = String(Math.abs(offset) % 60).padStart(2, "0");

    return `*All times displayed on this page are based on the user's local browser time (UTC: ${sign}${hours}:${minutes}).`;
  };

  const getDateStringWithOffset = (date) => {
    const dateString = date.toLocaleString("en-GB", {
      hour12: false,
    });

    const offsetMinutes = date.getTimezoneOffset();
    const sign = offsetMinutes <= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const minutes = String(absMinutes % 60).padStart(2, "0");

    return `${dateString} UTC: ${sign}${hours}:${minutes}`;
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    setCurrentPage(0); // Reset page
  };

  const handleEndDateChange = (newValue) => {
    setToDate(newValue);
    setCurrentPage(0); // Reset page
  };

  useEffect(() => {
    setColumns(auditLevel === 0 ? Level0columns : Level1columns);
    setData([]);
    setExportType("");
    setCurrentPage(0);
    setItemsPerPage(10);
    setFromDate(dayjs().subtract(3, "month"));
    setToDate(dayjs());
    fetchResults(searchQuery, 0, 10);
  }, [auditLevel]);

  useEffect(() => {
    setSelectedBox(false);
  }, [auditLevel, currentPage, itemsPerPage, searchQuery, fromDate, toDate]);

  useEffect(() => {
    fetchResults(searchQuery, currentPage, itemsPerPage);
  }, []);

  const config = {
    headers: {},
  };

  const mapLevel0Data = (
    data = [],
    pageNumber = 0,
    pageSize = 10,
    isExport = false
  ) => {
    return data.map((item, index) => {
      const base = {
        sno: isExport ? index + 1 : pageNumber * pageSize + index + 1,
        batOneId: item.batOneId,
        userName: item.userName,
        accountCreation: formatDate(item.accountCreationDate),
        activeRoles: item.activeRoles,
        accountStatus: item.accountStatus,
        accountInactivation: formatDate(item.accountInactivationDate),
        lastLoginDate: formatDate(item.lastActivityDate),
      };

      return isExport
        ? base
        : {
            ...base,
            checked: false,
            profileAttribute: item.profileAttribute,
            countryCode: item.countryCode,
          };
    });
  };

  const mapLevel1Data = (
    data = [],
    pageNumber = 0,
    pageSize = 10,
    isExport = false
  ) => {
    return data.map((item, index) => {
      const base = {
        sno: isExport ? index + 1 : pageNumber * pageSize + index + 1,
        batOneId: item.batOneId,
        userName: item.username,
        functionality: item.functionalityName,
        action: item.action,
        entityTableName: item.tableName,
        primaryId: item.entityId,
        fieldName: item.fieldName,
        oldValue: returnIfDate(item.oldValue),
        newValue: returnIfDate(item.newValue),
        dateTime: formatDate(item.auditDateTime),
      };

      return isExport ? base : { ...base, checked: false };
    });
  };

  const fetchResults = useCallback(
    debounce(async (query, pageNumber = 0, pageSize = itemsPerPage) => {
      try {
        if (auditLevel === 0) {
          const payload = {
            pageNumber,
            pageSize,
          };

          if (query.trim()) {
            payload.commaSeparatedUsers = query.trim();
          }

          const response = await ReportsService.getLevel0Data(payload, config);

          const responseData = response?.data?.response;

          const mappedData = mapLevel0Data(
            responseData?.Level0ReportDto,
            pageNumber,
            pageSize
          );

          setTotalItems(responseData.totalItems);
          setData(mappedData);
          setNoData(!Boolean(mappedData?.length));
        } else {
          setLoading(true);
          const payload = {
            pageNumber,
            pageSize,
            fromDate: dayjs(fromDate).format("YYYY-MM-DD"),
            ...(toDate && { toDate: dayjs(toDate).format("YYYY-MM-DD") }),
            batOneId: selectedUser?.batOneId,
          };

          config.headers.apiCountryCode =
            selectedUser.profileAttribute === "Local"
              ? selectedUser.countryCode
              : "Global";

          const response = await ReportsService.getLevel1Data(payload, config);

          const responseData = response?.data?.response;

          const mappedData = mapLevel1Data(
            responseData?.level1ReportData,
            pageNumber,
            pageSize
          );

          setTotalItems(responseData.totalItems);
          setData(mappedData);
          setNoData(!Boolean(mappedData?.length));
        }
      } catch (error) {
        console.error("Error fetching data:", error.message);
        setSeverity("error");
        setMessage(
          error?.response?.data?.errorMessage || error?.response?.data?.message
        );
        setOpenAlert(true);
        setData([]);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    }, 500),
    [auditLevel, itemsPerPage, fromDate, toDate]
  );

  const fetchExportData = async () => {
    try {
      const payload = {};

      if (auditLevel === 0) {
        payload.isBulkExport = true;

        const response = await ReportsService.getLevel0ExportData(
          payload,
          config
        );

        const responseData = response.data.response;

        const mappedData = mapLevel0Data(responseData, 0, 0, true);
        exportData.current = mappedData;
      } else {
        const payload = {
          batOneId: selectedUser.batOneId,
          fromDate: dayjs(fromDate).format("YYYY-MM-DD"),
          ...(toDate && { toDate: dayjs(toDate).format("YYYY-MM-DD") }),
        };

        config.headers.apiCountryCode =
          selectedUser.profileAttribute === "Local"
            ? selectedUser.countryCode
            : "Global";

        const response = await ReportsService.getLevel1ExportData(
          payload,
          config
        );

        const responseData = response.data.response;

        const mappedData = mapLevel1Data(responseData, 0, 0, true);
        exportData.current = mappedData;
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
      setSeverity("error");
      setMessage(error?.response?.data?.message || error?.message);
      setOpenAlert(true);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchResults(query);
  };

  const downloadCSV = async () => {
    if (!selectedFields) {
      await fetchExportData();
    }
    const csvRows = [];

    const headers = columns.filter((ele) => ele.field !== "checked");
    csvRows.push(headers.map((ele) => ele.label).join(","));

    let dataToExport;
    if (data.every((ele) => ele.checked === false)) {
      dataToExport = exportData.current;
    } else {
      dataToExport = data
        .filter((ele) => ele.checked === true)
        .map((ele, index) => ({
          ...ele,
          sno: index + 1,
        }));
    }

    for (const row of dataToExport) {
      delete row.checked;
      const values = headers.map((header) => `"${row[header.field] ?? ""}"`);
      csvRows.push(values.join(","));
    }

    csvRows.push("");
    csvRows.push(timeZoneNote());

    const now = new Date();
    csvRows.push(`Generated on:,${getDateStringWithOffset(now)}`);

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download =
      auditLevel === 0
        ? "Audit LOG - Level 0 Report.csv"
        : "Audit LOG - Level 1 Report.csv";
    a.click();
    URL.revokeObjectURL(url);

    setExportConfirmModal(false);
    setSeverity("success");
    setMessage(t("reports.exportsuccess"));
    setOpenAlert(true);
  };

  const handleDownloadPDF = async () => {
    if (!selectedFields) {
      await fetchExportData();
    }

    try {
      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      let page = pdfDoc.addPage([841.89, 595.28]); // Landscape orientation
      const { width, height } = page.getSize();

      // Start content after some spacing
      let yOffset = height - 50;

      // Calculate available width for the table
      const margin = 30;
      const availableWidth = width - 2 * margin; // Total available width minus margins

      // Conditional headers based on auditLevel
      let headers;

      if (auditLevel === 0) {
        // Original headers for auditLevel 0
        headers = [
          { label: t("reports.sno"), field: "sno", width: 50 },
          { label: t("reports.batOneId"), field: "batOneId", width: 70 },
          { label: t("reports.userName"), field: "userName", width: 100 },
          {
            label: t("reports.accountCreation"),
            field: "accountCreation",
            width: 110,
          },
          { label: t("reports.activeRoles"), field: "activeRoles", width: 100 },
          {
            label: t("reports.accountStatus"),
            field: "accountStatus",
            width: 70,
          },
          {
            label: t("reports.accountInactivation"),
            field: "accountInactivation",
            width: 110,
          },
          {
            label: t("reports.lastLoginDate"),
            field: "lastLoginDate",
            width: 110,
          },
        ];
      } else {
        // Level1columns headers for other auditLevel values
        headers = [
          { label: t("reports.sno"), field: "sno", width: 50 },
          { label: t("reports.batOneId"), field: "batOneId", width: 70 },
          { label: t("reports.userName"), field: "userName", width: 90 },
          {
            label: t("reports.functionality"),
            field: "functionality",
            width: 100,
          },
          { label: t("Action"), field: "action", width: 60 },
          {
            label: t("reports.entityTableName"),
            field: "entityTableName",
            width: 110,
          },
          { label: t("reports.primaryId"), field: "primaryId", width: 80 },
          { label: t("reports.fieldName"), field: "fieldName", width: 90 },
          { label: t("reports.oldValue"), field: "oldValue", width: 80 },
          { label: t("reports.newValue"), field: "newValue", width: 80 },
          { label: t("reports.dateTime"), field: "dateTime", width: 120 },
        ];
      }

      // Function to calculate minimum width needed for each header
      const calculateMinimumWidths = () => {
        const minWidths = [];
        headers.forEach((header) => {
          const textWidth = helveticaBold.widthOfTextAtSize(header.label, 10);
          minWidths.push(Math.max(textWidth + 15, 35)); // 15px padding + minimum 35px width
        });
        return minWidths;
      };

      // Get minimum required widths for headers
      const minWidths = calculateMinimumWidths();
      const totalMinWidth = minWidths.reduce((sum, width) => sum + width, 0);

      // Always ensure total width doesn't exceed available width
      const totalHeaderWidth = headers.reduce(
        (sum, header) => sum + header.width,
        0
      );

      if (totalHeaderWidth > availableWidth || totalMinWidth > availableWidth) {
        // Use minimum widths as base and scale proportionally
        const baseWidths =
          totalMinWidth > availableWidth
            ? minWidths
            : headers.map((h) => h.width);
        const totalBaseWidth = baseWidths.reduce(
          (sum, width) => sum + width,
          0
        );

        let remainingWidth = availableWidth;

        headers.forEach((header, index) => {
          if (index === headers.length - 1) {
            // Last column gets remaining width to ensure exact fit
            header.width = remainingWidth;
          } else {
            header.width = Math.floor(
              (baseWidths[index] / totalBaseWidth) * availableWidth
            );
            remainingWidth -= header.width;
          }
          // Ensure minimum width of 30px for any column
          header.width = Math.max(header.width, 30);
        });

        // Final adjustment to ensure total doesn't exceed available width
        const finalTotal = headers.reduce(
          (sum, header) => sum + header.width,
          0
        );
        if (finalTotal > availableWidth) {
          const excess = finalTotal - availableWidth;
          // Reduce from the largest columns first
          const sortedIndices = headers
            .map((h, i) => ({ width: h.width, index: i }))
            .sort((a, b) => b.width - a.width);

          let remainingExcess = excess;
          for (const { index } of sortedIndices) {
            if (remainingExcess <= 0) break;
            const reduction = Math.min(
              remainingExcess,
              headers[index].width - 30
            );
            headers[index].width -= reduction;
            remainingExcess -= reduction;
          }
        }
      }

      // Calculate column positions with exact width control
      const startX = margin;
      let currentX = startX;
      const columnPositions = [];

      // Ensure total width equals available width exactly
      const actualTotalWidth = headers.reduce(
        (sum, header) => sum + header.width,
        0
      );

      headers.forEach((header, index) => {
        columnPositions.push({ x: currentX, width: header.width });
        currentX += header.width;
      });

      // Use available width as table width to ensure it fits exactly
      const tableWidth = Math.min(actualTotalWidth, availableWidth);

      // Function to wrap text for headers
      const wrapHeaderText = (text, maxWidth, font, size) => {
        if (!text) return [""];

        const textStr = String(text);
        const maxWidthForText = maxWidth - 10; // 10px total padding

        if (font.widthOfTextAtSize(textStr, size) <= maxWidthForText) {
          return [textStr];
        }

        // Try to break on spaces first
        const words = textStr.split(" ");
        if (words.length > 1) {
          const lines = [];
          let currentLine = "";

          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const lineWidth = font.widthOfTextAtSize(testLine, size);

            if (lineWidth <= maxWidthForText) {
              currentLine = testLine;
            } else {
              if (currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                // Single word is too long, but don't truncate - just use it
                lines.push(word);
              }
            }
          }

          if (currentLine) {
            lines.push(currentLine);
          }

          return lines.length > 0 ? lines : [textStr];
        }

        // If it's a single word and too long, don't truncate - just return it
        return [textStr];
      };

      // Function to draw table header (matching MUI table header style)
      const drawTableHeader = (y) => {
        // Calculate header height based on wrapped text
        let maxHeaderLines = 1;
        const wrappedHeaders = [];

        headers.forEach((header, index) => {
          const pos = columnPositions[index];
          const wrapped = wrapHeaderText(
            header.label,
            pos.width,
            helveticaBold,
            10
          );
          wrappedHeaders.push(wrapped);
          maxHeaderLines = Math.max(maxHeaderLines, wrapped.length);
        });

        const headerHeight = Math.max(40, maxHeaderLines * 12 + 16); // Dynamic height based on text

        // Header background - matching #0A3575 from StyledTableCell
        page.drawRectangle({
          x: startX,
          y: y - headerHeight,
          width: tableWidth,
          height: headerHeight,
          color: rgb(0.04, 0.21, 0.46), // #0A3575 converted to RGB
        });

        // Draw header text with white color and bold font (matching MUI styling)
        headers.forEach((header, index) => {
          const pos = columnPositions[index];
          const lines = wrappedHeaders[index];

          lines.forEach((line, lineIndex) => {
            // Center align text in header cells
            const textWidth = helveticaBold.widthOfTextAtSize(line, 10);
            const centerX = pos.x + (pos.width - textWidth) / 2;

            page.drawText(line, {
              x: Math.max(pos.x + 5, centerX), // Ensure minimum padding
              y: y - 18 - lineIndex * 12, // Adjust for multiple lines
              size: 10,
              font: helveticaBold,
              color: rgb(1, 1, 1), // White text
            });
          });
        });

        // Draw vertical lines between columns (light gray borders)
        columnPositions.forEach((pos, index) => {
          if (index > 0) {
            page.drawLine({
              start: { x: pos.x, y: y },
              end: { x: pos.x, y: y - headerHeight },
              thickness: 0.5,
              color: rgb(0.8, 0.8, 0.8),
            });
          }
        });

        // Draw outer border
        page.drawRectangle({
          x: startX,
          y: y - headerHeight,
          width: tableWidth,
          height: headerHeight,
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.5,
        });

        return y - headerHeight - 2;
      };

      // Function to wrap text with better width management
      // Function to wrap text with better width management
      const wrapText = (text, maxWidth, font, size) => {
        if (!text && text !== false) return [""];

        let textStr;
        if (Array.isArray(text)) {
          textStr = text.join(", ");
        } else if (typeof text === "boolean") {
          textStr = text ? "Yes" : "No"; // Simple Yes/No for checkboxes
        } else {
          textStr = String(text);
        }

        // Filter out any non-ASCII characters that might cause encoding issues
        textStr = textStr.replace(/[^\x20-\x7E]/g, "");

        // If text is too long, try to wrap it
        const maxWidthForText = maxWidth - 6; // 6px total padding (3px each side)

        if (font.widthOfTextAtSize(textStr, size) <= maxWidthForText) {
          return [textStr];
        }

        const words = textStr.split(" ");
        const lines = [];
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const lineWidth = font.widthOfTextAtSize(testLine, size);

          if (lineWidth <= maxWidthForText) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              currentLine = word;

              // Check if the single word is still too long for the next line
              if (font.widthOfTextAtSize(word, size) > maxWidthForText) {
                // Break the long word into smaller chunks
                const brokenWord = breakLongWord(
                  word,
                  maxWidthForText,
                  font,
                  size
                );
                lines.push(...brokenWord.slice(0, -1)); // Add all but the last chunk
                currentLine = brokenWord[brokenWord.length - 1]; // Keep last chunk as current line
              }
            } else {
              // Single word is too long, break it into chunks
              const brokenWord = breakLongWord(
                word,
                maxWidthForText,
                font,
                size
              );
              lines.push(...brokenWord.slice(0, -1)); // Add all but the last chunk
              currentLine = brokenWord[brokenWord.length - 1]; // Keep last chunk as current line
            }
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines.length > 0 ? lines : [""];
      };

      // Helper function to break long words into chunks that fit
      const breakLongWord = (word, maxWidth, font, size) => {
        const chunks = [];
        let remainingWord = word;

        while (remainingWord.length > 0) {
          let chunkSize = remainingWord.length;

          // Find the maximum number of characters that fit
          while (
            chunkSize > 0 &&
            font.widthOfTextAtSize(
              remainingWord.substring(0, chunkSize),
              size
            ) > maxWidth
          ) {
            chunkSize--;
          }

          // Ensure we take at least one character to avoid infinite loop
          chunkSize = Math.max(1, chunkSize);

          chunks.push(remainingWord.substring(0, chunkSize));
          remainingWord = remainingWord.substring(chunkSize);
        }

        return chunks;
      };
      // Function to draw table row (matching MUI table row style)
      const drawTableRow = (rowData, y, isAlternate = false) => {
        const baseRowHeight = 35; // Matching 50px from StyledTableCell

        // Calculate row height based on wrapped text
        let maxLines = 1;
        const wrappedTexts = [];

        headers.forEach((header, index) => {
          const pos = columnPositions[index];
          const cellData = rowData[header.field];
          const wrapped = wrapText(cellData, pos.width, helveticaFont, 11);
          wrappedTexts.push(wrapped);
          maxLines = Math.max(maxLines, wrapped.length);
        });

        const rowHeight = Math.max(baseRowHeight, maxLines * 14 + 10);

        // Check if we need a new page
        if (y - rowHeight < 50) {
          page = pdfDoc.addPage([841.89, 595.28]);
          y = height - 50;
          // Redraw header on new page
          y = drawTableHeader(y);
          y -= 5;
        }

        // Row background - alternating white (#ffffff) for odd rows
        const bgColor = isAlternate ? rgb(0.98, 0.98, 0.98) : rgb(1, 1, 1);

        page.drawRectangle({
          x: startX,
          y: y - rowHeight,
          width: tableWidth,
          height: rowHeight,
          color: bgColor,
        });

        // Draw cell content
        headers.forEach((header, index) => {
          const pos = columnPositions[index];
          const lines = wrappedTexts[index];

          lines.forEach((line, lineIndex) => {
            let textX = pos.x + 3; // Reduced padding

            // Center align for checkbox column and S.No
            if (header.field === "checked" || header.field === "sno") {
              const textWidth = helveticaFont.widthOfTextAtSize(line, 9);
              textX = pos.x + (pos.width - textWidth) / 2;
            }

            // Special styling for userName (blue underlined text)
            let textColor = rgb(0.1, 0.12, 0.19); // #191E31 from StyledTableCell
            let font = helveticaFont;

            page.drawText(line, {
              x: textX,
              y: y - 16 - lineIndex * 12,
              size: 9, // Reduced font size to fit better
              font: helveticaBold, // 600 font weight
              color: textColor,
            });
          });
        });

        // Draw row borders
        page.drawRectangle({
          x: startX,
          y: y - rowHeight,
          width: tableWidth,
          height: rowHeight,
          borderColor: rgb(0.9, 0.9, 0.9),
          borderWidth: 0.5,
        });

        // Draw vertical lines for columns
        columnPositions.forEach((pos, index) => {
          if (index > 0) {
            page.drawLine({
              start: { x: pos.x, y: y },
              end: { x: pos.x, y: y - rowHeight },
              thickness: 0.5,
              color: rgb(0.9, 0.9, 0.9),
            });
          }
        });

        return y - rowHeight - 1; // 1px spacing between rows
      };

      // Draw title with similar styling (conditional title based on auditLevel)
      const reportTitle =
        auditLevel === 0 ? "Audit Log - Level 0" : "Audit Log - Level 1";
      page.drawText(reportTitle, {
        x: startX,
        y: yOffset,
        size: 16,
        font: helveticaBold,
        color: rgb(0.04, 0.21, 0.46), // Matching header color
      });
      yOffset -= 35;

      // Draw table header
      yOffset = drawTableHeader(yOffset);
      yOffset -= 5;

      let dataToExport;

      if (data.every((ele) => ele.checked === false)) {
        dataToExport = exportData.current;
      } else {
        dataToExport = data
          .filter((ele) => ele.checked === true)
          .map((ele, index) => ({
            ...ele,
            sno: index + 1,
          }));
      }
      // Draw table rows with alternating background
      dataToExport.forEach((row, index) => {
        delete row?.checked;
        // Match MUI table's nth-of-type(odd) styling
        const isOddRow = (index + 1) % 2 === 1;
        yOffset = drawTableRow(row, yOffset, !isOddRow); // Invert for proper alternating
      });

      // Add footer with timestamp (styled like MUI secondary text)
      const now = new Date();

      // Go to last page to add footer
      const pages = pdfDoc.getPages();
      const lastPage = pages[pages.length - 1];

      lastPage.drawText(timeZoneNote(), {
        x: startX,
        y: 50,
        size: 10,
        font: helveticaFont,
        color: rgb(1, 0, 0),
      });

      lastPage.drawText(`Generated on: ${getDateStringWithOffset(now)}`, {
        x: startX,
        y: 30,
        size: 10,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5), // Secondary text color
      });

      // Save and download PDF with conditional filename
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        auditLevel === 0
          ? "Audit LOG - Level 0 Report.pdf"
          : "Audit LOG - Level 1 Report.pdf";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSeverity("success");
      setMessage(t("reports.exportsuccess"));
      setOpenAlert(true);
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setExportConfirmModal(false);
    }
  };

  const formatDate = (isoDateString) => {
    if (!isoDateString) return null;

    const date = new Date(isoDateString);

    return date
      .toLocaleString("en-GB", {
        // timeZone: "UTC",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  const returnIfDate = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => returnIfDate(item));
    }

    if (typeof value !== "string") return value;

    if (/^-?\d+(\.\d+)?$/.test(value)) return value;

    let date;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
      date = new Date(value.replace(" ", "T"));
    } else if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+\-]\d{2}:\d{2})$/.test(
        value
      )
    ) {
      date = new Date(value);
    } else if (
      /^[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{2}:\d{2}:\d{2} IST \d{4}$/.test(value)
    ) {
      const fixed = value.replace(" IST ", " GMT+0530 ");
      date = new Date(fixed);
    } else if (/^\d{2}\/\d{2}\/\d{4}T\d{2}:\d{2}:\d{2}$/.test(value)) {
      const [d, m, rest] = value.split("/");
      const [y, time] = rest.split("T");
      date = new Date(`${y}-${m}-${d}T${time}`);
    } else {
      return value;
    }

    return !isNaN(date.getTime()) ? formatDate(date) : value;
  };

  return (
    <LayoutAccessManagement>
      <Box sx={{ paddingBottom: "100px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: -1.5,
            mb: 2,
          }}
        >
          <Button
            sx={{
              borderRadius: 100,
              minWidth: 95,
              minHeight: 44,
              background: "#102A62",
              display: auditLevel === 0 && "none",
            }}
            disabled={loading}
            variant="contained"
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => auditLevel === 1 && setAuditLevel(0)}
          >
            Back
          </Button>
          <Typography
            sx={{
              ...commonStyle,
              fontSize: "20px",
              color: "#737D86",
            }}
          >
            Audit Log - Level {auditLevel}
          </Typography>
          <Box>
            {auditLevel === 0 ? (
              <TextField
                sx={{
                  width: "403px",
                  "& .MuiOutlinedInput-root": {
                    height: "51px",
                    "& fieldset": { border: "none" },
                  },
                  "& input::placeholder": {
                    fontStyle: "italic",
                  },
                  "& input:-webkit-autofill": {
                    boxShadow: "0 0 0 100px white inset !important",
                    WebkitTextFillColor: "inherit",
                    caretColor: "inherit",
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={handleSearch}
                placeholder={t("search")}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }} gap={"20px"}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={fromDate}
                    onChange={handleFromDateChange}
                    maxDate={toDate} // Limit start date
                    format="DD-MM-YYYY" // Add this line to format the date
                    sx={{
                      width: "165px",
                      "& .MuiOutlinedInput-input":{
                       paddingRight:"0px !important"
                      },
                      "& .MuiOutlinedInput-root": {
                        height: "46px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        ...commonDateStyle,
                        "& input": {
                          cursor: "pointer",
                          pointerEvents: "none", // This prevents the input from capturing clicks
                        },
                        "& fieldset": {
                          borderColor: "#C4C4C4",
                          borderRadius: "5px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#C4C4C4",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#C4C4C4",
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: (e) =>
                          e.currentTarget.querySelector("button").click(), // This triggers the calendar
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            color: "#00000099",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            fontSize: "18px",
                            lineHeight: "20px",
                            letterSpacing: "0%",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={toDate}
                    onChange={handleEndDateChange}
                    minDate={fromDate} // Limit end date
                    format="DD-MM-YYYY" // Add this line to format the date
                    sx={{
                      width: "165px",
                      "& .MuiOutlinedInput-input":{
                       paddingRight:"0px !important"
                      },
                      "& .MuiOutlinedInput-root": {
                        height: "46px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        ...commonDateStyle,
                        "& input": {
                          cursor: "pointer",
                          pointerEvents: "none", // This prevents the input from capturing clicks
                        },
                        "& fieldset": {
                          borderColor: "#C4C4C4",
                          borderRadius: "5px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#C4C4C4",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#C4C4C4",
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: (e) =>
                          e.currentTarget.querySelector("button").click(), // This triggers the calendar
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            color: "#00000099",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            fontSize: "18px",
                            lineHeight: "20px",
                            letterSpacing: "0%",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Button
                  sx={{
                    borderRadius: "10px",
                    width: "100px",
                    height: "43px",
                    background: "#102A62",
                    ...commonStyle,
                    fontSize: "16px",
                  }}
                  variant="contained"
                  onClick={() => {
                    fetchResults("", 0);
                  }}
                  disabled={loading}
                >
                  {loading && <CircularProgress size={20} />}
                  {t("reports.go")}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {!noData ? (
          <>
            <ReportsTable
              columns={columns}
              rows={data}
              setData={setData}
              auditLevel={auditLevel}
              setAuditLevel={setAuditLevel}
              setSelectedUser={setSelectedUser}
              selectedBox={selectedBox}
              setSelectedBox={setSelectedBox}
            />
            <Box
              sx={{
                display: data.length === 0 ? "none" : "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                mt: 2,
                mb: -1,
              }}
            >
              <FormControl
                sx={{
                  m: 0.5,
                  mr: 2,
                  minWidth: { xs: 150, md: 300 },
                  minHeight: 50,
                }}
              >
                <TextField
                  select
                  value={exportType || "Select Format"}
                  onChange={(e) => setExportType(e.target.value)}
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  placeholder="Select Format"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { border: "none" }, // removes border
                      boxShadow: "0 1px 4px lightgray",
                    },
                    "& .MuiSelect-select": {
                      fontStyle: exportType ? "normal" : "italic",
                      color: !exportType && "lightgray",
                    },
                  }}
                >
                  <MenuItem
                    key="SelectFormat"
                    value="Select Format"
                    sx={{ display: "none" }}
                  >
                    Select Format
                  </MenuItem>
                  <MenuItem key="csv" value="csv" sx={commonStyle}>
                    CSV
                  </MenuItem>
                  <MenuItem key="pdf" value="pdf" sx={commonStyle}>
                    PDF
                  </MenuItem>
                </TextField>
              </FormControl>
              <Button
                sx={{
                  borderRadius: "10px",
                  background: "#EF7D00",
                  color: "#ffffff !important",
                  ...commonStyle,
                  fontSize: "16px",
                  height: 54,
                  width: { xs: "100%", md: "156px" },
                  opacity: exportType ? 1 : 0.4,
                  boxShadow: "0 3px 5px gray",
                  "&:hover": {
                    backgroundColor: "#EF7D00",
                  },
                }}
                disabled={!exportType}
                onClick={() => {
                  setSelectedFields(
                    data.filter((ele) => ele.checked === true).length
                  );
                  setExportConfirmModal(true);
                }}
              >
                {t("reports.exportAll")}
              </Button>
            </Box>
            <div
              // style={{
              //   marginTop:"-10px",
              //   position: "absolute",
              //   width: "98%",
              //   bottom: "-70px", // Changed from 70px to 0
              // }}
              style={{
                position: "fixed",
                width: "96vw", // Full viewport width
                bottom: "0px", // Stick to bottom of viewport
                left: "0", // Start from left edge
                zIndex: 1000,
                padding: "0 2vw",
                backgroundColor: "white", // Add background if needed
              }}
            >
              <CustomPagination
                itemsPerPage={itemsPerPage}
                setItemsPerPage={(v) => setItemsPerPage(v)}
                totalItems={totalItems}
                paginate={paginate}
                currentPage={currentPage}
                displayRecords
                timeZoneNote={timeZoneNote()}
              />
            </div>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "40px",
            }}
          >
            <img src={NoData} style={{ height: "100px" }} alt="No Data" />
            <p>{t("promptActionNotification.nodata")}</p>
          </Box>
        )}
      </Box>
      <ExportConfirmModal
        open={exportConfirmModal}
        selectedFields={selectedFields}
        onClose={() => setExportConfirmModal(false)}
        onConfirm={() => {
          exportType === "csv" ? downloadCSV() : handleDownloadPDF();
        }}
      />
    </LayoutAccessManagement>
  );
};

export default GetReports;
